import { MinHeap } from '@/service/utils/cache/minHeap';
import type { CacheServiceConfig } from '@/service/utils/cache/types';

export class CacheService<K extends string, D> {
    public cache;
    private timerHeap;
    private cleanupTimeout: ReturnType<typeof setTimeout> | null = null;
    private observers = new Map<K, {
        callbacks: Set<(data: D | undefined) => void>;
        activeSubscribers: number;
    }>();

    constructor({ cache, timers }: CacheServiceConfig<K, D>) {
        this.cache = cache;
        this.timerHeap = new MinHeap<K>(timers);
        this.scheduleNextCleanup();
    }

    private scheduleNextCleanup(): void {
        if (this.cleanupTimeout) {
            clearTimeout(this.cleanupTimeout);
        }

        const nextTimer = this.timerHeap.peek();
        if (nextTimer) {
            const delay = nextTimer.priority - Date.now();
            if (delay > 0) {
                this.cleanupTimeout = setTimeout(() => {
                    const expired = this.timerHeap.extractMin();
                    if (expired !== undefined) {
                        const observerEntry = this.observers.get(expired.key);

                        if (observerEntry && observerEntry.activeSubscribers > 0) {
                            observerEntry.callbacks.forEach(callback => callback(undefined));
                        }

                        if (!observerEntry || observerEntry.activeSubscribers === 0) {
                            this.cache.delete(expired.key);

                            if (observerEntry) {
                                this.observers.delete(expired.key);
                            }
                        }
                    }
                    this.scheduleNextCleanup();
                }, delay);
            }
        }
    }

    public set({ key, data, timer = 1000 }: { key: K; data: D; timer?: number }): void {
        const expireTime = Date.now() + timer;

        this.cache.set(key, data);

        this.timerHeap.insert(key, expireTime);

        const observerEntry = this.observers.get(key);
        if (observerEntry) {
            observerEntry.callbacks.forEach(callback => callback(data));
        }

        this.scheduleNextCleanup();
    }

    public delete({ key }: { key: K }, force: boolean = false): void {
        const observerEntry = this.observers.get(key);

        if (observerEntry) {
            observerEntry.callbacks.forEach(callback => callback(undefined)); // Уведомляем об удалении
        }

        if (force || !observerEntry || observerEntry.activeSubscribers === 0) {
            this.cache.delete(key);
        }

        this.scheduleNextCleanup();
    }

    public get({ key }: { key: K }): D | undefined {
        return this.cache.get(key);
    }

    public clearKeys(keys: K[], force: boolean = true): void {
        keys.forEach((key) => {
            this.delete({ key }, force);
        });
    }

    public subscribe(key: K, callback: (data: D | undefined) => void) {
        if (!this.observers.has(key)) {
            this.observers.set(key, { callbacks: new Set(), activeSubscribers: 0 });
        }
        const observerEntry = this.observers.get(key);

        if (observerEntry) {
            observerEntry.callbacks.add(callback);
            observerEntry.activeSubscribers++;
        }

        callback(this.cache.get(key));
    }

    public unsubscribe(key: K, callback: () => void) {
        const observerEntry = this.observers.get(key);
        if (!observerEntry) return;

        observerEntry.callbacks.delete(callback);
        observerEntry.activeSubscribers--;

        if (observerEntry.activeSubscribers === 0) {
            observerEntry.callbacks.clear();
        }
    }

    public clear(): void {
        this.cache.clear();
        this.observers.forEach((observerEntry) => {
            observerEntry.callbacks.forEach(callback => callback(undefined));
        });
        this.observers.clear();

        while (this.timerHeap.size() > 0) {
            this.timerHeap.extractMin();
        }
        if (this.cleanupTimeout) {
            clearTimeout(this.cleanupTimeout);
        }
    }
}
